import React, { useState } from 'react';
import PaidWith from './PaidWith';
import './NewItemsPrompt.css';
import './TradeBootPrompt.css';

// "Was cash involved in this trade?"
//
// WHY THIS HAS TO EXIST
// The extension can see that you traded a Karambit for an AK and a Talon,
// because Steam records which items moved. Steam does NOT record money: cash on
// the side of a trade changes hands on a marketplace, over a chat, anywhere but
// Steam. So an auto-linked trade knows everything about a trade except its price
// whenever the trade was uneven.
//
// The cost of getting this wrong is not cosmetic. What the received items cost
// you is what you gave up, cash included, and that figure is the baseline every
// future profit on those items is measured against. A trade where you added $200
// and the tracker assumed you added nothing will overstate your profit by $200
// for as long as you hold the item, and there is nothing later that would catch
// it. So the trade gets linked automatically, and the one fact that cannot be
// inferred gets asked.
//
// Dismissing is a real answer: most trades are even, and "no cash" leaves a cost
// basis that was already right exactly as it is.

export function TradeBootBanner({ count, onReview, onDismiss, variant = 'classic' }) {
  return (
    <div className={`nip-banner ${variant}`}>
      <span className="nip-banner-ico" aria-hidden="true">{variant === 'zone' ? 'Trade' : ''}</span>
      <span className="nip-banner-txt">
        <strong>{count} trade{count !== 1 ? 's' : ''}</strong> came in from Steam.{' '}
        Steam does not record money, so{' '}
        <button type="button" className="nip-banner-link" onClick={onReview}>
          say whether cash was involved
        </button>
      </span>
      <button type="button" className="nip-banner-x" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

export function TradeBootModal({ pending, onResolve, onDismissOne, onClose, trackWallets = true, useBank = true, variant = 'classic' }) {
  return (
    <div className={`nip-overlay ${variant}`} onClick={onClose}>
      <div className="nip-panel" onClick={e => e.stopPropagation()}>
        <div className="nip-head">
          <div className="nip-head-txt">
            <h3 className="nip-title">Cash on these trades</h3>
            <p className="nip-sub">
              Steam records the items, never the money. If a trade was even, say so and nothing changes.
            </p>
          </div>
          <button type="button" className="nip-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="nip-body">
          {pending.length === 0 && (
            <p className="tbp-empty">Nothing left to answer.</p>
          )}
          {pending.map(trade => (
            <TradeBootRow
              key={trade.tradeId}
              trade={trade}
              variant={variant}
              trackWallets={trackWallets}
              useBank={useBank}
              onResolve={onResolve}
              onDismissOne={onDismissOne}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TradeBootRow({ trade, variant, trackWallets, useBank, onResolve, onDismissOne }) {
  // Same split as the trade modal: a positive amount plus a direction, rather
  // than asking anyone to type a negative number into a money field.
  const [dir, setDir] = useState('received');
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);

  const value = parseFloat(amount) || 0;

  const save = async () => {
    setBusy(true);
    try { await onResolve(trade.tradeId, value * (dir === 'paid' ? -1 : 1), source); }
    finally { setBusy(false); }
  };

  const when = trade.at ? new Date(trade.at).toLocaleDateString('en-US',
    { month: 'short', day: 'numeric', year: 'numeric' }) : 'n/a';

  const zone = variant === 'zone';

  return (
    <div className="tbp-row">
      <div className="tbp-pair">
        {trade.gave} <span className="tbp-arrow">for</span>{' '}
        {trade.received?.length ? trade.received.join(', ') : 'n/a'}
      </div>
      <div className="tbp-meta">
        {when}{trade.tradedWith ? ` · with ${trade.tradedWith}` : ''}
      </div>

      <div className="tbp-controls">
        {[
          { id: 'received', label: 'I received' },
          { id: 'paid',     label: 'I paid' },
        ].map(d => (
          <button
            key={d.id}
            type="button"
            className={`tbp-dir ${dir === d.id ? 'on' : ''}`}
            onClick={() => setDir(d.id)}
          >
            {d.label}
          </button>
        ))}

        <span className="tbp-amount">
          <span className="tbp-amount-sign">$</span>
          <input
            type="number" min="0" step="0.01" placeholder="0.00"
            value={amount} onChange={e => setAmount(e.target.value)}
          />
        </span>
      </div>

      {value > 0 && trackWallets && (
        <div className="tbp-controls">
          <span className="tbp-source-label">
            {dir === 'paid' ? 'Paid from' : 'Received into'}
          </span>
          <PaidWith
            value={source}
            onChange={setSource}
            accent={zone ? '#131313' : '#818cf8'}
            zone={zone}
            allowBank={useBank}
          />
        </div>
      )}

      <div className="tbp-actions">
        <button type="button" className="tbp-save" disabled={busy || value <= 0} onClick={save}>
          Save
        </button>
        <button type="button" className="tbp-skip" disabled={busy} onClick={() => onDismissOne(trade.tradeId)}>
          No cash involved
        </button>
      </div>
    </div>
  );
}
